import { useMemo } from 'react';
import { Search_RequestBody } from '@opensearch-project/opensearch/api';
import { ResponseBody } from '@opensearch-project/opensearch/api/_types/_core.search';

import { useSelectedStore } from '@/hooks';
import { EnumCustomerType, OpenSearchProduct, TreezStore } from '@/types';
import { useEcomStoreSelector } from '@/data';
import { getDefaultStore } from '@/utils';
import { ORIGIN_BACKEND_HEADER } from '@/constants';
import { mapProduct } from 'utils/opensearch';

import { injectCustomerTypeToSearchBody } from '../utils';

const openSearchApiEndpoint =
  process.env.NEXT_PUBLIC_OPEN_SEARCH_ENDPOINT || '';
const openSearchApiKey =
  process.env.NEXT_PUBLIC_OPEN_SEARCH_API_KEY ||
  'jbS0CPECEy8u1LVZcqgqg4lTYCdy6nJtBkEl4GK2';

class SearchService {
  private _entityId: string | undefined;
  private _orgId: string | undefined;
  private _customerType: EnumCustomerType | undefined;
  private _isStrictMedicalEnabled: boolean | undefined;

  constructor(
    _entityId?: string,
    _orgId?: string,
    _customerType?: EnumCustomerType,
    _isStrictMedicalEnabled?: boolean,
  ) {
    const defaultStore = getDefaultStore();

    this._entityId = _entityId ?? defaultStore?.msoStoreEntityId;
    this._orgId = _orgId ?? defaultStore?.msoOrgId;
    this._customerType = _customerType;
    this._isStrictMedicalEnabled = _isStrictMedicalEnabled;
  }

  search = async ({
    searchBody,
    revalidate = 60,
  }: {
    searchBody: Search_RequestBody;
    revalidate?: number;
  }) => {
    const body = injectCustomerTypeToSearchBody({
      searchBody,
      customerType: this._customerType,
      isStrictMedicalEnabled: this._isStrictMedicalEnabled,
    });
    const stringifiedBody = JSON.stringify(body);

    const headers = {
      'Content-Type': 'application/json',
      'entity-id': this._entityId ?? '',
      'org-id': this._orgId ?? '',
      'X-Api-Key': openSearchApiKey ?? '',
      ...(process.env.X_API_ORIGIN_BACKEND
        ? { [ORIGIN_BACKEND_HEADER]: process.env.X_API_ORIGIN_BACKEND }
        : {}),
    };

    const response = await fetch(openSearchApiEndpoint, {
      method: 'POST',
      headers,
      body: stringifiedBody,
      next: { tags: ['opensearch'], revalidate },
      cache: 'force-cache',
    });

    if (!response.ok) {
      console.error('SearchService search error:', {
        status: response.status,
        statusText: response.statusText,
        json: await response.json(),
      });
      throw new Error('Network response was not ok');
    }

    return response.json() as Promise<ResponseBody>;
  };

  getProductBySlug = async ({ slug }: { slug: string }) => {
    const searchBody: Search_RequestBody = {
      from: 0,
      size: 1,
      query: {
        match: {
          slug,
        },
      },
    };

    const response = await this.search({ searchBody });

    if (response.hits.hits.length === 0) return undefined;

    return mapProduct(response.hits.hits[0]._source as OpenSearchProduct);
  };

  getProductsByCategory = async ({
    category,
    pageSize = 20,
  }: {
    category: string;
    pageSize?: number;
  }) => {
    const searchBody: Search_RequestBody = {
      from: 0,
      size: pageSize,
      query: {
        bool: {
          should: [
            {
              match: {
                'category.keyword': category,
              },
            },
          ],
        },
      },
    };

    const response = await this.search({ searchBody });

    return response.hits.hits.map(hit =>
      mapProduct(hit._source as OpenSearchProduct),
    );
  };
}

type Args = {
  selectedStore?: TreezStore;
  selectedCustomerType?: EnumCustomerType;
};

export const useSearchService = (args?: Args) => {
  const { store } = useSelectedStore();
  const isStrictMedicalEnabled = store?.strict_medical_enabled;
  const { customer_type } = useEcomStoreSelector(['customer_type']);
  const storeToUse = store || args?.selectedStore;
  const customerTypeToUse = customer_type || args?.selectedCustomerType;

  const searchService = useMemo(() => {
    return new SearchService(
      storeToUse?.msoStoreEntityId,
      storeToUse?.msoOrgId,
      customerTypeToUse,
      isStrictMedicalEnabled,
    );
  }, [storeToUse?.msoStoreEntityId, storeToUse?.msoOrgId, customerTypeToUse]);

  return searchService;
};

export { SearchService };
